import { Link } from 'react-router-dom';
import { Scan } from 'lucide-react';

const footerLinks = {
  product: [
    { label: 'Documents', href: '/documents/', external: false },
    { label: 'Solutions', href: '/solutions/', external: false },
    { label: 'Integrations', href: '/integrations/', external: false },
    { label: 'Compare', href: '/compare/', external: false },
    { label: 'Blog', href: '/blog/', external: false },
  ],
  company: [
    { label: 'About', href: '/blog/about-scandocpro/', external: false },
    { label: 'SendFaxPro', href: 'https://sendfax.pro', external: true },
    { label: '4AI Teknoloji', href: 'https://4aiteknoloji.com', external: true },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy/', external: false },
    { label: 'Terms of Service', href: '/terms/', external: false },
  ],
};

// Security: noopener noreferrer on all external links
const ExternalLink = ({ href, children, className }: { href: string; children: React.ReactNode; className?: string }) => (
  <a 
    href={href} 
    target="_blank" 
    rel="noopener noreferrer"
    className={className}
  >
    {children}
  </a>
);

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-500 rounded-xl flex items-center justify-center mr-3">
                <Scan className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                ScanDocPro
              </span>
            </Link>
            <p className="text-gray-600 dark:text-gray-400 text-sm max-w-xs mb-6">
              The intelligent document scanner in your pocket. Part of the 4AI Utility Bundle.
            </p>
            <div className="flex space-x-4">
              <ExternalLink
                href="https://4aiteknoloji.com"
                className="w-10 h-10 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:border-primary-300 dark:hover:border-primary-700 transition-colors"
                aria-label="4AI Teknoloji"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </ExternalLink>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <ExternalLink
                      href={link.href}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </ExternalLink>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <ExternalLink
                      href={link.href}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </ExternalLink>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {currentYear} 4AI Info Tech All rights reserved.
          </p>
          <ExternalLink
            href="https://4aiteknoloji.com"
            className="text-sm text-gray-400 dark:text-gray-500 mt-2 md:mt-0 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            Made with <span className="mx-1">❤️</span> in Ankara
          </ExternalLink>
        </div>
      </div>
    </footer>
  );
}
